import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FiBook, FiCheck, FiX, FiTrash2 } from 'react-icons/fi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TABS = ['pending', 'sourced', 'already_in_catalog', 'declined'];
const STATUS_BADGE = {
  pending: 'bg-yellow-100 text-yellow-700',
  sourced: 'bg-green-100 text-green-700',
  already_in_catalog: 'bg-blue-100 text-blue-700',
  declined: 'bg-red-100 text-red-700',
};

export default function BookSuggestionsTab({ getAuthHeader }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [actioningId, setActioningId] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/book-suggestions${filter ? `?status_filter=${filter}` : ''}`,
        { headers: getAuthHeader() }
      );
      setItems(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filter, getAuthHeader]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const updateStatus = async (id, status) => {
    let note = null;
    if (status !== 'pending') {
      note = window.prompt(`Optional note for marking as "${status}":`, '');
      if (note === null) return; // cancelled
    }
    setActioningId(id);
    try {
      await axios.post(`${API}/admin/book-suggestions/${id}/status`,
        { status, admin_note: note },
        { headers: getAuthHeader() }
      );
      await fetchItems();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    } finally {
      setActioningId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this suggestion permanently?')) return;
    setActioningId(id);
    try {
      await axios.delete(`${API}/admin/book-suggestions/${id}`, { headers: getAuthHeader() });
      await fetchItems();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    } finally {
      setActioningId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="book-suggestions-tab">
      <div className="flex items-center gap-3">
        <FiBook className="w-6 h-6 text-purple-600" />
        <h2 className="text-2xl font-bold text-gray-800">Book Suggestions</h2>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 text-sm rounded-full capitalize ${
              filter === s ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            data-testid={`suggestions-filter-${s}`}
          >{s.replace(/_/g, ' ')}</button>
        ))}
        <button
          onClick={() => setFilter('')}
          className={`px-3 py-1.5 text-sm rounded-full ${
            filter === '' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          data-testid="suggestions-filter-all"
        >All</button>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">
          No {filter || 'book'} suggestions.
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="py-3 px-4">Title / Author</th>
                <th className="py-3 px-4">Submitter</th>
                <th className="py-3 px-4 text-right">Votes</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-t hover:bg-gray-50" data-testid={`suggestion-row-${s.id}`}>
                  <td className="py-3 px-4">
                    <p className="font-semibold text-gray-800">{s.title}</p>
                    <p className="text-xs text-gray-500">by {s.author}</p>
                    {s.admin_note && <p className="text-xs text-gray-500 mt-1 italic">Note: {s.admin_note}</p>}
                  </td>
                  <td className="py-3 px-4">
                    <p className="text-gray-800">{s.submitted_by_name}</p>
                    <p className="text-xs text-gray-500">{s.submitted_by_email}</p>
                    <p className="text-xs text-gray-400">{new Date(s.created_at).toLocaleDateString()}</p>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className="inline-block min-w-[2rem] text-center px-2 py-0.5 rounded bg-purple-100 text-purple-700 font-bold">
                      {s.vote_count}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${STATUS_BADGE[s.status] || 'bg-gray-100 text-gray-700'}`}>
                      {(s.status || 'pending').replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-end gap-1 flex-wrap">
                      {s.status !== 'sourced' && (
                        <button
                          onClick={() => updateStatus(s.id, 'sourced')}
                          disabled={actioningId === s.id}
                          className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                          data-testid={`mark-sourced-${s.id}`}
                        ><FiCheck className="w-3 h-3" /> Sourced</button>
                      )}
                      {s.status !== 'already_in_catalog' && (
                        <button
                          onClick={() => updateStatus(s.id, 'already_in_catalog')}
                          disabled={actioningId === s.id}
                          className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                          data-testid={`mark-in-catalog-${s.id}`}
                        >In catalog</button>
                      )}
                      {s.status !== 'declined' && (
                        <button
                          onClick={() => updateStatus(s.id, 'declined')}
                          disabled={actioningId === s.id}
                          className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                          data-testid={`mark-declined-${s.id}`}
                        ><FiX className="w-3 h-3" /> Decline</button>
                      )}
                      <button
                        onClick={() => remove(s.id)}
                        disabled={actioningId === s.id}
                        className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded disabled:opacity-50"
                        data-testid={`delete-${s.id}`}
                        title="Delete permanently"
                      ><FiTrash2 className="w-3 h-3" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
